import { useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { CASE_AI_ASSISTANTS } from '@app/constants/permissions';
import { agentEventProcessor } from '@app/helpers/agent-event-processor';
import { eventProcessor } from '@app/helpers/event-processor';
import { jobEventProcessor } from '@app/helpers/job-event-processor';
import usePermissionVerify from '@app/hooks/permission-verify';
import { useAppSelector } from '@app/hooks/redux';
import { useUnaddressed } from '@app/hooks/use-unaddressed';

interface TopicMessageEvent {
  detail?: {
    payload?: Array<{
      name: string;
      payload: unknown;
      userId: string;
    }>;
  };
}

const useTopicHandler = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const userId = useAppSelector(
    (state) => state?.auth?.user?._id || state?.auth?.user?.id || '',
  );
  const permissions = useAppSelector((state) => state?.auth?.permissions || []);
  const permissionsMap = usePermissionVerify([CASE_AI_ASSISTANTS]);
  const isFabricAgentsEnabled = !!permissionsMap[CASE_AI_ASSISTANTS];
  const { unaddressedEnabled, suppressionEnabled } = useUnaddressed(userId);
  const memoizedTopicHandler = useMemo(
    () =>
      (
        messageEvent: CustomEvent<{
          payload?: Array<{ name: string; payload: unknown; userId: string }>;
        }>,
      ) => {
        const event: TopicMessageEvent = messageEvent;
        if (isFabricAgentsEnabled) {
          agentEventProcessor(queryClient)(event);
        }
        jobEventProcessor(
          queryClient,
          userId,
          permissions,
          unaddressedEnabled,
          suppressionEnabled,
        )(event);
        eventProcessor(dispatch, queryClient)(event);
      },
    [
      dispatch,
      queryClient,
      userId,
      permissions,
      isFabricAgentsEnabled,
      unaddressedEnabled,
      suppressionEnabled,
    ],
  );
  return memoizedTopicHandler;
};
export default useTopicHandler;
